<script lang="ts" setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination, Navigation } from 'swiper/modules'
import Lightbox from 'vue-easy-lightbox'
import { useStoreConfig } from '~/store/storeConfig'
import ProductImg from '~/components/Product/ProductImg.vue'

const placeholderImgSrc = '/product-placeholder.png'
const props = defineProps<{
  mediaGallery: string[]
  productVariant?: number
  name?: string
}>()

const gallery = computed(() => (props.mediaGallery.length ? props.mediaGallery : [placeholderImgSrc]))

const { isMobile } = useStoreConfig()
const image = useImage()
const { isAlt, isTarpininke } = useTheme()
const activeImage = ref(0)
const showLightbox = ref(false)
const swiperModules = [Navigation, Pagination]

const optimizedImages = computed(() =>
  gallery.value.map((img) =>
    image(img, {
      format: 'webp',
      quality: 95,
      height: 1000,
    })
  )
)
const getSlideCount = (count: number) => {
  if (isMobile) {
    return 1
  }

  return isAlt ? 3 : count
}

const swiperBreakpoints = {
  1025: {
    slidesPerView: getSlideCount(4),
  },
  1160: {
    slidesPerView: getSlideCount(5),
  },
  1530: {
    slidesPerView: getSlideCount(6),
  },
}
watch(toRef(props, 'productVariant'), () => (activeImage.value = 0))

const openLightbox = (isEnabled?: boolean) => {
  if (isEnabled) {
    showLightbox.value = true
  }
}
</script>

<template>
  <div class="preview-gallery">
    <button
      v-if="!isMobile"
      class="preview flex items-center justify-center w-full dark:bg-white dark:rounded-sm dark:overflow-hidden"
      @click="openLightbox(true)">
      <ProductImg :src="gallery[activeImage]" class="product-img" size="lg" :alt="`${props.name}` ?? ''" />
    </button>
    <div v-if="!(!isMobile && !mediaGallery.length)" class="thumbnails-container lg:relative">
      <div>
        <ClientOnly>
          <Swiper
            :slides-per-view="getSlideCount(5)"
            :space-between="isTarpininke ? 0 : 5"
            :modules="swiperModules"
            :breakpoints="swiperBreakpoints"
            :navigation="true"
            :pagination="{ clickable: true }"
            :prevent-clicks-propagation="false"
            :threshold="5">
            <SwiperSlide v-for="(thumbnail, index) in gallery" :key="index">
              <div
                class="slide-item flex items-center justify-center"
                :class="{ active: index === activeImage }"
                @click="activeImage = index">
                <ProductImg
                  :src="thumbnail"
                  size="lg"
                  :alt="`${props.name} ${index}`"
                  @click="openLightbox(isMobile)" />
              </div>
            </SwiperSlide>
          </Swiper>
        </ClientOnly>
      </div>
    </div>
    <Lightbox
      v-if="mediaGallery.length"
      :visible="showLightbox"
      :imgs="optimizedImages"
      :index="activeImage"
      @hide="showLightbox = false" />
  </div>
</template>

<style lang="postcss">
.preview-gallery {
  .preview {
    img {
      width: 100%;
      object-fit: contain;
    }
  }

  .thumbnails-container {
    margin: 10px auto;

    .slide-item {
      @apply bg-white;

      img {
        max-height: 343px;
      }
    }

    .swiper {
      padding-bottom: 3rem;

      .swiper-pagination {
        display: block;
      }
    }
  }

  .vel-toolbar .toolbar-btn {
    font-size: 32px;
  }
}

@media screen and (min-width: 1025px) {
  .preview-gallery {
    .preview {
      max-width: 600px;
      height: 600px;
      margin: 0 auto;
      user-select: none;

      img {
        max-width: 600px;
        max-height: 600px;
      }
    }

    .swiper {
      position: static;
      margin: 0 51px !important;
      padding-bottom: 0 !important;

      .swiper-pagination {
        display: none !important;
      }
    }

    .slide-item {
      margin: auto;
      width: 5.3rem !important;
      height: 4rem !important;
      border: 1px solid theme('colors.neutral5');
      border-radius: 0.25rem;
      cursor: pointer;

      &.active {
        border-color: theme('colors.brand.primary');
        border-width: 2px;
      }

      img {
        height: 54px !important;
        user-select: none;
        -webkit-user-drag: none;
      }
    }

    .swiper-button-prev,
    .swiper-button-next {
      bottom: 8px !important;

      &::after {
        height: 36px;
        width: 36px;
      }
    }

    .vel-toolbar .toolbar-btn {
      font-size: 20px;
    }
  }
}
</style>
